<app-dialog
  #dialog
  [key]="'onboarding-v2-view'"
  [action]="'onboarding-v2-view'"
  [headerTheme]="'simple'"
  [title]="
    data ? data.corporateName || data.tradeName || 'Visualizar Onboarding' : 'Visualizar Onboarding'
  "
  [width]="'1000px'"
  [maxWidth]="'96vw'"
  [maxHeight]="'92vh'"
  [appendTo]="'body'"
  [hideDefaultFooter]="true"
  (onClose)="close()">
  <div class="ov2">
    @if (isDataLoading) {
      <div class="ov2__loading">
        <app-cog-loading-spinner />
      </div>
    }

    @if (!isDataLoading && data) {
      <!-- Barra de progresso geral -->
      <div class="ov2__progress">
        <div class="ov2__progress__top">
          <div class="ov2__progress__left">
            <span class="ov2__progress__value">{{ progress }}%</span>
            <span class="ov2__progress__legend"
              >{{ completedCount }} de {{ totalStages }} etapas concluídas</span
            >
          </div>
          <button
            class="ov2__download-btn"
            type="button"
            [disabled]="isDownloading || !hasDownloadableDocuments"
            (click)="downloadAllDocuments()"
            title="Baixar todos os documentos em ZIP">
            @if (isDownloading) {
              <i class="pi pi-spinner pi-spin"></i>
            } @else {
              <i class="pi pi-download"></i>
            }
            {{ isDownloading ? 'Gerando ZIP...' : 'Baixar todos' }}
          </button>
        </div>
        <div class="ov2__progress__bar">
          <div class="ov2__progress__bar__fill" [style.width.%]="progress"></div>
        </div>
      </div>

      <!-- Seções: uma por etapa -->
      <div class="ov2__sections">
        @for (stage of data.onboardingProcess?.stages; track stage.name; let i = $index) {
          <div class="ov2__section" [class.ov2__section--open]="isSectionOpen(i)">
            <div class="ov2__section-header" (click)="toggleSection(i)">
              <span class="ov2__acc-title">
                @if (isStageEffectivelyCompleted(stage)) {
                  <i class="pi pi-check-circle ov2__acc-title__icon--done"></i>
                } @else {
                  <i class="pi pi-circle ov2__acc-title__icon--pending"></i>
                }
                {{ getStageLabel(stage.name ?? '') }}
                @if (isStageEffectivelyCompleted(stage)) {
                  <span class="ov2__chip ov2__chip--done">Concluída</span>
                } @else if (isStageDesirable(stage)) {
                  <span class="ov2__chip ov2__chip--desirable">Desejável</span>
                } @else {
                  <span class="ov2__chip">Pendente</span>
                }
              </span>
              <i
                class="pi ov2__section-chevron"
                [class.pi-chevron-up]="isSectionOpen(i)"
                [class.pi-chevron-down]="!isSectionOpen(i)"></i>
            </div>

            @if (isSectionOpen(i)) {
              <div class="ov2__section-content">
                <!-- Etapa 1: Informações da empresa -->
                @if (stage.name === 'CompanyInformation') {
                  <div class="ov2__grid">
                    <div class="ov2__field">
                      <span class="ov2__field__label">Razão Social</span>
                      <span class="ov2__field__value">{{ data.corporateName || '—' }}</span>
                    </div>
                    <div class="ov2__field">
                      <span class="ov2__field__label">Nome Fantasia</span>
                      <span class="ov2__field__value">{{ data.tradeName || '—' }}</span>
                    </div>
                    <div class="ov2__field">
                      <span class="ov2__field__label">CNPJ</span>
                      <span class="ov2__field__value">{{ data.cnpj || '—' }}</span>
                    </div>
                    <div class="ov2__field">
                      <span class="ov2__field__label">Setor</span>
                      <span class="ov2__field__value">{{ data.sector || '—' }}</span>
                    </div>
                    <div class="ov2__field">
                      <span class="ov2__field__label">Site</span>
                      <span class="ov2__field__value">{{ data.website || '—' }}</span>
                    </div>
                    <div class="ov2__field">
                      <span class="ov2__field__label">Holding</span>
                      <span class="ov2__field__value">{{ data.hasHolding ? 'Sim' : 'Não' }}</span>
                    </div>
                    @if (data.address) {
                      <div class="ov2__field ov2__field--full">
                        <span class="ov2__field__label">Endereço</span>
                        <span class="ov2__field__value">
                          {{ data.address }}{{ data.city ? ', ' + data.city : ''
                          }}{{ data.state ? ' — ' + data.state : ''
                          }}{{ data.zipCode ? ' · CEP ' + data.zipCode : '' }}
                        </span>
                      </div>
                    }
                  </div>
                }

                <!-- Etapa 2: Contatos -->
                @else if (stage.name === 'Contacts') {
                  <div class="ov2__contacts">
                    <div class="ov2__contact-group">
                      <span class="ov2__contact-group__title">Contatos da empresa</span>
                      @if (!data.companyContacts?.length) {
                        <p class="ov2__empty">Nenhum contato informado.</p>
                      } @else {
                        @for (c of data.companyContacts; track $index) {
                          <div class="ov2__contact-card">
                            <span class="ov2__contact-card__name">{{ c.name }}</span>
                            <span class="ov2__contact-card__detail">{{ c.email }}</span>
                            @if (c.phone) {
                              <span class="ov2__contact-card__detail">{{ c.phone }}</span>
                            }
                            @if (c.areaPosition) {
                              <span class="ov2__contact-card__role">{{ c.areaPosition }}</span>
                            }
                          </div>
                        }
                      }
                    </div>

                    <div class="ov2__contact-group">
                      <span class="ov2__contact-group__title">Contatos contábeis</span>
                      @if (!data.accountingContacts?.length) {
                        <p class="ov2__empty">Nenhum contato informado.</p>
                      } @else {
                        @for (c of data.accountingContacts; track $index) {
                          <div class="ov2__contact-card">
                            <span class="ov2__contact-card__name">{{ c.name }}</span>
                            <span class="ov2__contact-card__detail">{{ c.email }}</span>
                            @if (c.phone) {
                              <span class="ov2__contact-card__detail">{{ c.phone }}</span>
                            }
                            @if (c.areaPosition) {
                              <span class="ov2__contact-card__role">{{ c.areaPosition }}</span>
                            }
                          </div>
                        }
                      }
                    </div>

                    <div class="ov2__contact-group">
                      <span class="ov2__contact-group__title">Contatos bancários</span>
                      @if (!data.bankingContacts?.length) {
                        <p class="ov2__empty">Nenhum contato informado.</p>
                      } @else {
                        @for (c of data.bankingContacts; track $index) {
                          <div class="ov2__contact-card">
                            <span class="ov2__contact-card__name">{{ c.name }}</span>
                            <span class="ov2__contact-card__detail">{{ c.email }}</span>
                            @if (c.phone) {
                              <span class="ov2__contact-card__detail">{{ c.phone }}</span>
                            }
                            @if (c.areaPosition) {
                              <span class="ov2__contact-card__role">{{ c.areaPosition }}</span>
                            }
                          </div>
                        }
                      }
                    </div>

                    <div class="ov2__contact-group">
                      <span class="ov2__contact-group__title">Contatos financeiros</span>
                      @if (!data.financialContacts?.length) {
                        <p class="ov2__empty">Nenhum contato informado.</p>
                      } @else {
                        @for (c of data.financialContacts; track $index) {
                          <div class="ov2__contact-card">
                            <span class="ov2__contact-card__name">{{ c.name }}</span>
                            <span class="ov2__contact-card__detail">{{ c.email }}</span>
                            @if (c.phone) {
                              <span class="ov2__contact-card__detail">{{ c.phone }}</span>
                            }
                            @if (c.areaPosition) {
                              <span class="ov2__contact-card__role">{{ c.areaPosition }}</span>
                            }
                          </div>
                        }
                      }
                    </div>
                  </div>
                }

                <!-- Etapas 3–8: documentos -->
                @else {
                  @if (!stage.requiredDocuments?.length) {
                    <p class="ov2__empty">Nenhum documento requerido nesta etapa.</p>
                  } @else {
                    <div class="ov2__doc-list">
                      @for (doc of stage.requiredDocuments; track doc.name) {
                        <div class="ov2__doc">
                          <div class="ov2__doc__header">
                            <span class="ov2__doc__name">{{ getDocumentDisplayName(doc) }}</span>
                            <div class="ov2__doc__tags">
                              @if (doc.isRequired) {
                                <span class="ov2__tag ov2__tag--required">Obrigatório</span>
                              }
                              @if (doc.hasAnnualData && doc.requiredYearsOfData > 0) {
                                <span class="ov2__tag ov2__tag--count">
                                  {{ getValidSubmissionsCount(doc) }}/{{ doc.requiredYearsOfData }}
                                  anos
                                </span>
                              } @else {
                                <span class="ov2__tag ov2__tag--count">
                                  {{ getValidSubmissionsCount(doc) }} arquivo(s)
                                </span>
                              }
                            </div>
                          </div>

                          @if (doc.submittedDocuments?.length) {
                            <div class="ov2__submissions">
                              @for (sub of doc.submittedDocuments; track sub.id) {
                                <div class="ov2__submission">
                                  <i class="pi pi-file-pdf ov2__submission__icon"></i>
                                  @if (sub.documentLink) {
                                    <button
                                      type="button"
                                      class="ov2__submission__name ov2__submission__name--link"
                                      title="Visualizar documento"
                                      (click)="openSubmissionPreview(sub)">
                                      {{ getSubmissionLabel(sub) }}
                                    </button>
                                  } @else {
                                    <span class="ov2__submission__name">
                                      {{ getSubmissionLabel(sub) }}
                                    </span>
                                  }
                                  <span
                                    class="ov2__status-chip ov2__status-chip--{{
                                      getSubmissionStatusKey(sub.statusAnalysisDocuments)
                                    }}">
                                    {{ getSubmissionStatusLabel(sub.statusAnalysisDocuments) }}
                                  </span>
                                  @if (sub.documentLink) {
                                    <a
                                      [href]="sub.documentLink"
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      class="ov2__submission__dl"
                                      title="Baixar">
                                      <i class="pi pi-download"></i>
                                    </a>
                                  }
                                  @if (sub.id) {
                                    <button
                                      type="button"
                                      class="ov2__submission__remove"
                                      aria-label="Remover arquivo"
                                      title="Remover arquivo"
                                      [disabled]="isRemoving(sub)"
                                      (click)="removeSubmission(doc, sub)">
                                      <i
                                        class="pi"
                                        [class.pi-trash]="!isRemoving(sub)"
                                        [class.pi-spinner]="isRemoving(sub)"
                                        [class.pi-spin]="isRemoving(sub)"></i>
                                    </button>
                                  }
                                </div>
                              }
                            </div>
                          } @else {
                            <p class="ov2__doc__empty">Nenhum arquivo enviado.</p>
                          }
                        </div>
                      }
                    </div>
                  }
                }
              </div>
            }
          </div>
        }
      </div>
    }
  </div>
</app-dialog>
